import { createGlobalStyle } from "styled-components";

import "normalize.css/normalize.css";
import "./Global.scss";
import "./screens/editor/EditorHome.scss";
import "@fontsource/inter";
import "@fontsource/inter/variable-full.css";

const GlobalStyle = createGlobalStyle`
  html {
    /* keep colors in sync with global.scss */
    /* Core colors */
    --indigo0: #1C1040;
    --indigo1: #4929A6;
    --indigo2: #5430BF;
    --indigo3: #5F36D9;
    --indigo3-rgb: 95, 54, 217;
    --indigo4: #7D55F2;
    --indigo5: #9673FF;
    --indigo6: #BCA6FF;
    --indigo7: #F5F2FF;

    --green0: #034003;
    --green1: #067306;
    --green2: #078C07;
    --green3: #08A608;
    --green4: #1DBF1D;
    --green5: #36D936;
    --green6: #6DF26D;
    --green7: #F2FFF2;


    --orange5: #FFB366;

    --gold5: #FFC940;
    --gold6: #FFD773;
    --gold7: #FFF4D9;

    --dark-gray1: #1B2023;
    --dark-gray2: #242A2F;
    --dark-gray3: #2F363C;
    --dark-gray4: #363F47;
    --dark-gray5: #404A52;

    --gray1: #6D6E6F;
    --gray2: #848484;
    --gray3: #999999;
    --gray3-rgb: 153, 153, 153;
    --gray4: #B2B5B7;
    --gray5: #C7CBCE;

    --light-gray1: #D5D7D9;
    --light-gray2: #DEE0E2;
    --light-gray3: #E5E7E9;
    --light-gray4: #EEF0F2;
    --light-gray5: #F7F8F8;
    --light-gray5-rgb: 247, 248, 248;

    --intent-danger: #DB3737;

    /* Global colors */
    --global-purple: var(--indigo3);
    --global-purple-highlight: #9501A1;
    --global-dark-purple: var(--indigo0);
    --global-icon-purple: #441972;
    --global-red: #E05D5B;
    --global-green: #2ED02A;
    --global-yellow: #FFF177;
    --global-light-gray: var(--light-gray5);
    --global-mid-gray: var(--gray1);
    --global-dark-gray: var(--dark-gray3);;
    --global-blue-gray: var(--gray2);
    --global-off-white: #F7F8F9;
    --global-black: #0C0426;
    --global-warning-color: #D9822B;
    --global-background-color: var(--light-gray5);

    --global-match-text: var(--gold6);
    --global-match-backdrop: var(--gold7);

    /* Gradients */
    --indigo-gradient-start: #4C1876;
    --indigo-gradient-end: #120A29;
    --indigo-gradient-0: linear-gradient( 0deg, var(--indigo-gradient-start) 35%, var(--indigo-gradient-end) 100% );
    --indigo-gradient-65: linear-gradient( 65deg, var(--indigo-gradient-start) 35%, var(--indigo-gradient-end) 100% );
    --indigo-gradient-90: linear-gradient( 90deg, var(--indigo-gradient-start) 35%, var(--indigo-gradient-end) 100% );
    --indigo-gradient-115: linear-gradient( 115deg, var(--indigo-gradient-start) 35%, var(--indigo-gradient-end) 100% );
    --indigo-gradient-180: linear-gradient( 180deg, var(--indigo-gradient-start) 35%, var(--indigo-gradient-end) 100% );
    --indigo-gradient-245: linear-gradient( 245deg, var(--indigo-gradient-start) 35%, var(--indigo-gradient-end) 100% );
    --indigo-gradient-270: linear-gradient( 270deg, var(--indigo-gradient-start) 35%, var(--indigo-gradient-end) 100% );

    /*
      Editor colors
      We need purples that match the gradient for headers
    */
    --global-file-browser-purple: var(--indigo-gradient-start);
    --global-editor-header-purple: #5B2F80;

    /* Links */
    --link-color: var(--indigo3);
    --link-color-hover: var(--indigo1);
    --dark-link-color: var(--indigo6);
    --dark-link-color-hover: var(--indigo5);

    /* Buttons */
    --button-indigo: var(--indigo3);
    --button-indigo-hover: var(--indigo2);
    --button-indigo-active: var(--indigo1);

    --dark-button-indigo: var(--indigo6);
    --dark-button-indigo-hover: var(--indigo5);
    --dark-button-indigo-active: var(--indigo4);

    --button-green: var(--green3);
    --button-green-hover: var(--green2);
    --button-green-active: var(--green1);

    --dark-button-green: var(--green6);
    --dark-button-green-hover: var(--green5);
    --dark-button-green-active: var(--green4);

    /* Global styles */
    --global-content-width: 1200px;

    --global-font-size-xs: 12px;
    --global-font-size-s: 16px;
    --global-font-size-m: 20px;
    --global-font-size-l: 24px;
    --global-font-size-xl: 28px;

    --global-spacing-xs: 2px;
    --global-spacing-s: 4px;
    --global-spacing-m: 12px;
    --global-spacing-l: 20px;
    --global-spacing-xl: 40px;
    --global-spacing-xxl: 60px;

    --global-spacing-navbar-height: 50px;

    --global-border-radius: 12px;

    --global-font-family: "InterVariable", "Inter", sans-serif;
    /* changing monospace in the monaco-editors is only possible within the component */
    --global-font-family-monospace: "Hack", "Menlo", "Courier New", monospace;
    --global-font-weight-light: 300;
    --global-font-weight-regular: 400;
    --global-font-weight-bold: 600;
  }

  em, i {
    font-style: oblique 7.5deg;
  }

  strong, b {
    font-weight: var(--global-font-weight-bold);
  }

  body {
    background-color: var(--global-background-color);
    font-family: var(--global-font-family);
    font-weight: var(--global-font-weight-regular);
    line-height: 1.625;
    font-size: var(--global-font-size-s);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

    @media
    (-webkit-max-device-pixel-ratio: 1.5),
    (max-resolution: 128dpi) {
      /* Get font-smoothing back to normal in the editor only on non-retina screens */
      .monaco-editor {
        -webkit-font-smoothing: auto;
        -moz-osx-font-smoothing: auto;
      }
    }

  a {
    color: var(--link-color);

    :hover {
      color: var(--link-color-hover);
    }

    :active {
      color: var(--link-color-active);
    }
  }

  pre,
  code {
    font-family: var(--global-font-family-monospace);
  }
`;

export default GlobalStyle;
